import React, { useEffect, useState } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Switch, FormControl, FormControlLabel, InputLabel, Select, ListItemButton, MenuItem, Typography, Stack } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import BusinessIcon from '@mui/icons-material/Business';
import { getMyOffices } from '../../../api/userService';
import { useNavigate, useLocation } from "react-router-dom";
import { switchOffice } from '../../../api/authService';
import { useAuth } from '../../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../context/SnackbarContext';

export default function OfficeSwitcher({onClose = null}) {
    const { t } = useTranslation('common');
    const { handleLogin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();

    const [open, setOpen] = React.useState(false);

    const [offices, setOffices] = useState(null);

    const [selectedOfficeId, setSelectedOfficeId] = useState("");
    const [selectedOfficeName, setSelectedOfficeName] = useState("");

    const [isDefault, setIsDefault] = useState(false);

    const activeOfficeName = localStorage.getItem('activeOfficeName');

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setSelectedOfficeId("");
        setSelectedOfficeName("");
        setIsDefault(false);
        if (onClose) onClose();
    };

    const fetchOffices = async () => {
      const offices = await getMyOffices();
      setOffices(offices);
    };

    const onLoginSuccess = () => {
      if (location.pathname === "/dashboard") {
        // Already on dashboard, force refresh
        window.location.reload();
      } else {
        // Navigate normally
        navigate("/dashboard");
      }
    };

    const handleSelect = (e) => {
      const officeId = e.target.value;
      setSelectedOfficeId(officeId);

      const office = offices.find(o => o.id === officeId);
      setSelectedOfficeName(office?.name || "");
    };

    const handleChange = async () => {
      try {
          const res = await switchOffice({
            office_id : Number(selectedOfficeId),
            set_as_default: isDefault
          }); 
          handleLogin(res.token, onLoginSuccess); // update role + auth state
          showSnackbar(`Successfully switched to ${selectedOfficeName}`, "success");
          handleClose();
      } catch (err) {
          if (err.status === 403)
          {
              showSnackbar(t('messages.account_disabled'), "error");
          } 
          else {
              showSnackbar(t('messages.login_fail'), "error");
          }
      }
    };

    useEffect(() => {
      if (open)
      {
        fetchOffices();
      }
    }, [open]);

    return (
        <React.Fragment>
        <ListItem>
            <ListItemButton onClick={handleClickOpen}>
            <ListItemIcon>
                <BusinessIcon fontSize='large' color='secondary'/>
            </ListItemIcon>
            <ListItemText 
                primary="Switch Clinic/Branch"
                secondary="switch to a different clinic or branch"
            />
            </ListItemButton>
        </ListItem>

        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            <DialogTitle>
            Switch Clinic/Branch
            </DialogTitle>
            <DialogContent sx={{ minHeight: 200 }}>
              {offices?.length === 1 ?
              <Stack
                direction="column"
                spacing={2}
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography component="span" variant="body2" color="text.primary">
                  no other clinic or branch available
                </Typography>
              </Stack>
              : 
              <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>
                  Select Clinic/Branch
                </InputLabel>
                <Select
                  value={selectedOfficeId}
                  label="Select Clinic/Branch"
                  onChange={handleSelect}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 250,
                      },
                    },
                  }}
                >
                  {offices?.map((office) => (
                    office.name !== activeOfficeName ?
                    <MenuItem key={office.id} value={office.id} sx={{ my: 1 }}>
                      {office.name}
                    </MenuItem>
                    : null
                  ))}
                </Select>

              </FormControl>
              
              { selectedOfficeId ?
              <FormControlLabel
                  control={
                      <Switch
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                      color="primary"
                      />
                  }
                  label="Set this as my default clinic/branch the next time I login."
              />
              : null }
              </Stack>
              }
            </DialogContent>
            <DialogActions>
              <div style={{flex: '1 0 0'}} />
              <Button 
                onClick={handleChange}
                disabled={!selectedOfficeId}
              >
                Swtich
              </Button>
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        </React.Fragment>
    );
}